import React, { useState, useMemo } from 'react';
import { Search, Loader2, X, Plus, ExternalLink, ShieldCheck, ShieldAlert, ShieldQuestion, CheckSquare, Square, Layers } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import logger from '@/lib/logger';

const verifyIcon = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'valid' || s === 'deliverable') return <ShieldCheck className="w-3 h-3 text-emerald-600" />;
  if (s === 'invalid' || s === 'undeliverable') return <ShieldAlert className="w-3 h-3 text-red-600" />;
  if (s) return <ShieldQuestion className="w-3 h-3 text-amber-600" />;
  return null;
};

/**
 * Hunter.io discovery dialog with two modes:
 *
 *   Single domain — type "stripe.com", see all known emails, add one at a time.
 *   Bulk domains  — paste up to 20 domains (one per line), see a flat list of
 *                   every email found across all domains, multi-select rows and
 *                   bulk-import them all as leads in one click.
 */
export function HunterDomainSearchDialog({ open, onClose, onCreated }) {
  const [mode, setMode] = useState('single');
  const [domain, setDomain] = useState('');
  const [limit, setLimit] = useState('10');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);            // single-mode result
  const [creatingIdx, setCreatingIdx] = useState(null);

  // Bulk mode state
  const [domainsText, setDomainsText] = useState('');
  const [bulkLimit, setBulkLimit] = useState('10');
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkStats, setBulkStats] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [autoVerify, setAutoVerify] = useState(true);

  const flatRows = useMemo(() => {
    if (!bulkResults) return [];
    const rows = [];
    bulkResults.forEach((g) => {
      if (g.error) return;
      (g.emails || []).forEach((e) => {
        rows.push({
          ...e,
          domain: g.domain,
          organization: g.organization || g.domain,
          _key: `${g.domain}::${e.value}`,
        });
      });
    });
    return rows;
  }, [bulkResults]);

  const parsedDomains = useMemo(
    () => domainsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean).slice(0, 20),
    [domainsText],
  );

  if (!open) return null;

  // ── SINGLE MODE ─────────────────────────────────────────────────────────
  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!domain.trim() || loading) return;
    setLoading(true);
    setData(null);
    try {
      const { data: resp } = await api.hunterDomainSearch({
        domain: domain.trim(),
        limit: limit ? Number(limit) : undefined,
      });
      setData(resp);
      if (!resp.emails?.length) toast.info('No emails returned for that domain');
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || 'Domain search failed');
    } finally { setLoading(false); }
  };

  const handleAddAsLead = async (row, idx) => {
    if (creatingIdx !== null) return;
    setCreatingIdx(idx);
    try {
      const name = [row.first_name, row.last_name].filter(Boolean).join(' ').trim() || row.value;
      await api.createLead({
        name,
        email: row.value,
        company: data?.organization || data?.domain || '',
        website: data?.domain ? `https://${data.domain}` : '',
        status: 'new',
        category: '',
        lead_source: 'Hunter.io',
        tags: ['hunter-io'],
        custom_fields: {
          ...(row.position ? { Title: row.position } : {}),
          ...(row.department ? { Department: row.department } : {}),
          ...(row.seniority ? { Seniority: row.seniority } : {}),
        },
      });
      toast.success(`Added ${name} as a lead`);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add as lead');
    } finally { setCreatingIdx(null); }
  };

  // ── BULK MODE ───────────────────────────────────────────────────────────
  const handleBulkSearch = async (e) => {
    e?.preventDefault();
    if (parsedDomains.length === 0 || loading) return;
    setLoading(true);
    setBulkResults(null);
    setBulkStats(null);
    setSelectedKeys(new Set());
    try {
      const { data: resp } = await api.hunterBulkDomainSearch(parsedDomains, Number(bulkLimit) || 10);
      setBulkResults(resp.results || []);
      setBulkStats(resp.stats || null);
      const okCount = (resp.results || []).filter((r) => !r.error).length;
      if (resp.stats?.rate_limited) {
        toast.warning(`Hunter rate-limited — stopped after ${okCount} domains`);
      } else if (!resp.stats?.total_emails) {
        toast.info('No emails returned for those domains');
      } else {
        toast.success(`Found ${resp.stats.total_emails} emails across ${okCount} domains`);
      }
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || 'Bulk domain search failed');
    } finally { setLoading(false); }
  };

  const toggleKey = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedKeys.size === flatRows.length) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(flatRows.map((r) => r._key)));
  };

  const handleBulkImport = async () => {
    if (selectedKeys.size === 0 || importing) return;
    setImporting(true);
    try {
      const contacts = flatRows
        .filter((r) => selectedKeys.has(r._key))
        .map((r) => ({
          email: r.value,
          first_name: r.first_name,
          last_name: r.last_name,
          position: r.position,
          department: r.department,
          seniority: r.seniority,
          linkedin: r.linkedin,
          confidence: r.confidence,
          company: r.organization,
          domain: r.domain,
        }));
      const { data: resp } = await api.hunterBulkImportContacts(contacts, autoVerify);
      const parts = [];
      if (resp.created) parts.push(`${resp.created} created`);
      if (resp.skipped_dup) parts.push(`${resp.skipped_dup} duplicate`);
      if (resp.skipped_invalid) parts.push(`${resp.skipped_invalid} invalid`);
      const verifyNote = resp.auto_verify_queued ? ' · verifying in background' : '';
      toast.success(`Bulk import: ${parts.join(' · ') || 'nothing imported'}${verifyNote}`);
      setSelectedKeys(new Set());
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk import failed');
    } finally { setImporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="hunter-domain-search-dialog">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-[#FF7E0E]" />
            <h2 className="font-heading font-semibold text-slate-900 text-sm">Hunter.io Email Discovery</h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700 rounded-sm" data-testid="hunter-domain-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Mode tabs */}
        <div className="px-5 pt-3">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="h-8 p-0.5 bg-slate-100 rounded-sm" data-testid="hunter-mode-tabs">
              <TabsTrigger value="single" className="h-7 px-3 text-xs rounded-sm data-[state=active]:bg-white" data-testid="hunter-mode-single">
                <Search className="w-3 h-3 mr-1" /> Single Domain
              </TabsTrigger>
              <TabsTrigger value="bulk" className="h-7 px-3 text-xs rounded-sm data-[state=active]:bg-white" data-testid="hunter-mode-bulk">
                <Layers className="w-3 h-3 mr-1" /> Bulk Domains
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* SINGLE MODE — search form */}
        {mode === 'single' && (
          <form onSubmit={handleSearch} className="px-5 py-3 border-b border-slate-200 flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium">Company Domain</label>
              <Input autoFocus placeholder="stripe.com" value={domain} onChange={(e) => setDomain(e.target.value)} className="rounded-sm h-9 text-sm mt-1" data-testid="hunter-domain-input" />
            </div>
            <div className="w-full sm:w-20">
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium">Limit</label>
              <Input type="number" min={1} max={100} value={limit} onChange={(e) => setLimit(e.target.value)} className="rounded-sm h-9 text-sm mt-1" data-testid="hunter-domain-limit" />
            </div>
            <Button type="submit" disabled={loading || !domain.trim()} className="bg-[#FF7E0E] hover:bg-[#E5710C] text-white rounded-sm h-9" data-testid="hunter-domain-search-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Search className="w-3.5 h-3.5 mr-1" />}
              Search
            </Button>
          </form>
        )}

        {/* BULK MODE — paste textarea */}
        {mode === 'bulk' && (
          <form onSubmit={handleBulkSearch} className="px-5 py-3 border-b border-slate-200 flex flex-col gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium">
                Domains — one per line (max 20)
              </label>
              <textarea
                rows={4}
                placeholder="stripe.com&#10;reddit.com&#10;openai.com"
                value={domainsText}
                onChange={(e) => setDomainsText(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-sm text-sm font-mono focus:outline-none focus:border-[#FF7E0E] focus:ring-1 focus:ring-[#FF7E0E]/20"
                data-testid="hunter-bulk-domains-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {parsedDomains.length}/20 valid domain{parsedDomains.length === 1 ? '' : 's'} detected
              </p>
            </div>
            <div className="flex items-end gap-2">
              <div className="w-24">
                <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium">Per Domain</label>
                <Input type="number" min={1} max={100} value={bulkLimit} onChange={(e) => setBulkLimit(e.target.value)} className="rounded-sm h-9 text-sm mt-1" data-testid="hunter-bulk-limit" />
              </div>
              <Button type="submit" disabled={loading || parsedDomains.length === 0} className="bg-[#FF7E0E] hover:bg-[#E5710C] text-white rounded-sm h-9 ml-auto" data-testid="hunter-bulk-search-btn">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Search className="w-3.5 h-3.5 mr-1" />}
                Search {parsedDomains.length || ''}
              </Button>
            </div>
          </form>
        )}

        {/* Results pane */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {mode === 'single' && !data && !loading && (
            <p className="text-sm text-slate-400 italic py-12 text-center">
              Enter a company domain (e.g. <span className="font-mono">stripe.com</span>) to discover public emails.
            </p>
          )}
          {mode === 'bulk' && !bulkResults && !loading && (
            <p className="text-sm text-slate-400 italic py-12 text-center">
              Paste a list of company domains to bulk-discover contacts across all of them.
            </p>
          )}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          )}

          {/* SINGLE results */}
          {mode === 'single' && data && (
            <>
              <div className="mb-3 pb-3 border-b border-slate-100">
                <p className="text-sm font-medium text-slate-900">
                  {data.organization || data.domain}
                  {data.industry && <span className="text-slate-500 font-normal"> · {data.industry}</span>}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {data.emails?.length || 0} email{data.emails?.length === 1 ? '' : 's'} returned
                  {typeof data.total_results === 'number' && data.total_results > (data.emails?.length || 0)
                    && ` (of ${data.total_results} known)`}
                </p>
              </div>
              {data.emails?.length === 0 && (
                <p className="text-sm text-slate-400 italic py-4">No public emails found for that domain.</p>
              )}
              <div className="space-y-2">
                {data.emails?.map((row, i) => (
                  <div key={`${row.value}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm border border-slate-200 hover:bg-slate-50">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-sm">
                        <span className="font-medium text-[#0047AB] truncate">{row.value}</span>
                        {verifyIcon(row.verification?.status)}
                        {typeof row.confidence === 'number' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[9px] uppercase tracking-[0.1em] rounded-sm">{row.confidence}%</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {[row.first_name, row.last_name].filter(Boolean).join(' ') || '—'}
                        {row.position && <span> · {row.position}</span>}
                        {row.department && <span className="text-slate-400"> · {row.department}</span>}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {row.linkedin && (
                        <a href={row.linkedin} target="_blank" rel="noopener noreferrer" className="p-1.5 text-slate-400 hover:text-[#0047AB] rounded-sm" title="LinkedIn">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      <button
                        type="button"
                        onClick={() => handleAddAsLead(row, i)}
                        disabled={creatingIdx === i}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-[0.15em] font-medium rounded-sm border border-[#0047AB]/40 text-[#0047AB] bg-[#0047AB]/5 hover:bg-[#0047AB]/10 transition-colors disabled:opacity-60"
                        data-testid={`hunter-domain-add-${i}`}
                      >
                        {creatingIdx === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        Add as Lead
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* BULK results */}
          {mode === 'bulk' && bulkResults && (
            <>
              <div className="mb-3 pb-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-slate-900">
                    {bulkStats?.total_emails || 0} email{bulkStats?.total_emails === 1 ? '' : 's'} across {bulkStats?.domains_processed || 0} domain{bulkStats?.domains_processed === 1 ? '' : 's'}
                  </p>
                  {bulkStats?.failed > 0 && (
                    <p className="text-xs text-amber-600 mt-0.5">{bulkStats.failed} domain{bulkStats.failed === 1 ? '' : 's'} failed</p>
                  )}
                </div>
                {flatRows.length > 0 && (
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" title="After import, run Hunter Email Verifier on each new lead in the background. Uses 1 extra credit per lead.">
                      <input
                        type="checkbox"
                        checked={autoVerify}
                        onChange={(e) => setAutoVerify(e.target.checked)}
                        className="w-3.5 h-3.5 accent-[#FF7E0E]"
                        data-testid="hunter-bulk-auto-verify"
                      />
                      Auto-verify after import
                    </label>
                    <button type="button" onClick={toggleAll} className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1" data-testid="hunter-bulk-toggle-all">
                      {selectedKeys.size === flatRows.length ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                      {selectedKeys.size === flatRows.length ? 'Deselect all' : 'Select all'}
                    </button>
                    <Button
                      type="button"
                      onClick={handleBulkImport}
                      disabled={selectedKeys.size === 0 || importing}
                      className="bg-[#0047AB] hover:bg-[#003580] text-white rounded-sm h-8 text-xs"
                      data-testid="hunter-bulk-import-btn"
                    >
                      {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                      Import {selectedKeys.size || ''} as Lead{selectedKeys.size === 1 ? '' : 's'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Failed domains banner */}
              {bulkResults.filter((g) => g.error).length > 0 && (
                <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
                  Failed: {bulkResults.filter((g) => g.error).map((g) => g.domain).join(', ')}
                </div>
              )}

              {/* Flat rows grouped visually by domain */}
              {flatRows.length === 0 ? (
                <p className="text-sm text-slate-400 italic py-4">No emails returned across these domains.</p>
              ) : (
                <div className="space-y-1">
                  {bulkResults.filter((g) => !g.error && g.emails?.length).map((g) => (
                    <div key={g.domain} className="mb-3">
                      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium mb-1 mt-2">
                        {g.organization || g.domain} <span className="text-slate-400 font-normal">· {g.emails.length}</span>
                      </p>
                      <div className="space-y-1">
                        {g.emails.map((e) => {
                          const key = `${g.domain}::${e.value}`;
                          const checked = selectedKeys.has(key);
                          return (
                            <label
                              key={key}
                              className={`flex items-center gap-2 px-2 py-1.5 rounded-sm border cursor-pointer transition-colors ${checked ? 'bg-[#EFF6FF] border-[#0047AB]/30' : 'border-slate-200 hover:bg-slate-50'}`}
                              data-testid={`hunter-bulk-row-${e.value}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleKey(key)}
                                className="w-3.5 h-3.5 accent-[#0047AB]"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 text-sm">
                                  <span className="font-medium text-[#0047AB] truncate">{e.value}</span>
                                  {verifyIcon(e.verification?.status)}
                                  {typeof e.confidence === 'number' && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[9px] uppercase tracking-[0.1em] rounded-sm">{e.confidence}%</span>
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 truncate">
                                  {[e.first_name, e.last_name].filter(Boolean).join(' ') || '—'}
                                  {e.position && <span> · {e.position}</span>}
                                </p>
                              </div>
                              {e.linkedin && (
                                <a href={e.linkedin} target="_blank" rel="noopener noreferrer" onClick={(ev) => ev.stopPropagation()} className="p-1 text-slate-400 hover:text-[#0047AB]" title="LinkedIn">
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-slate-200 text-[10px] uppercase tracking-[0.15em] text-slate-400 flex items-center justify-between">
          <span>Powered by Hunter.io</span>
          <span>1 credit per ~10 results</span>
        </div>
      </div>
    </div>
  );
}
